import { cn } from "@/lib/utils";
import { CopyPlus, Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { canvasToolbarButtonVariants } from "../../../styles/canvas-toolbar-styles";
import { Button } from "../../ui/button";

export type AddToWorkspaceTriggerProps =
  ButtonHTMLAttributes<HTMLButtonElement> & {
    /**
     * `toolbar` is the compact lead verb inside `FileBar`; `pill` is the rust
     * CTA inside `GrowthPill`. Never both on one surface — the growth pill is
     * an alternative emphasis, not a second accent (public-view-toolbars
     * README rule 2), so the surface showing the pill drops its lead verb.
     */
    variant?: "toolbar" | "pill";
    busy?: boolean;
  };

/**
 * The viewer's copy verb, presentation only. Both surfaces render this so the
 * label, icon, and test id can't drift apart; the flow (picker, auth gate,
 * copy + poll + redirect) lives in `AddToWorkspaceAction`.
 *
 * Forwards its ref and spreads the rest: Radix `PopoverTrigger asChild` injects
 * `ref`, `onClick`, `aria-expanded`, and `data-state` here, and dropping any of
 * them silently kills the destination picker.
 */
export const AddToWorkspaceTrigger = forwardRef<
  HTMLButtonElement,
  AddToWorkspaceTriggerProps
>(function AddToWorkspaceTrigger(
  { variant = "toolbar", busy = false, className, ...props },
  ref,
) {
  const icon = busy ? (
    <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
  ) : (
    <CopyPlus className="size-3.5" aria-hidden="true" />
  );

  if (variant === "pill") {
    return (
      <Button
        ref={ref}
        type="button"
        variant="accent"
        size="sm"
        // Two overrides, both load-bearing: `size="sm"` is h-9 (36px) here
        // against the design system's 28px, and only <=32px keeps the pill on
        // its 48px bar-height formula. `[&_svg]:size-3.5` beats buttonVariants'
        // `[&_svg]:size-4` — a descendant selector the icon's own class cannot
        // outrank on specificity.
        className={cn(
          "h-7 shrink-0 rounded-sm px-3 [&_svg]:size-3.5",
          className,
        )}
        data-testid="public-canvas-add-to-workspace"
        {...props}
      >
        {busy ? icon : null}
        {/* The design system's canonical label (README rule 3). The toolbar
            variant keeps the shipped lowercase string so this port doesn't
            silently restyle copy on the existing surface. */}
        {busy ? "Adding…" : "Add to My Workspace"}
      </Button>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        canvasToolbarButtonVariants({ accent: true }),
        "px-2",
        className,
      )}
      data-testid="public-canvas-add-to-workspace"
      {...props}
    >
      {icon}
      {busy ? "Adding…" : "Add to my workspace"}
    </button>
  );
});
